import { IJkitTourTooltipSize, IJkitTourTooltipStyle, TJkitTourBreakpoint, TJkitTourEntry, TJkitTourStepPlacement } from './tour.model';

export class JkitTourHelpers {
	private static readonly _tooltipPadding = 12;
	private static readonly _tooltipOffset = 16;

	public static clampValue(value: number, min: number, max: number): number {
		if (max < min) return min;
		return Math.min(max, Math.max(min, value));
	}

	public static matchesEntry(entry: TJkitTourEntry, pathname: string, search: string = ''): boolean {
		const entries = Array.isArray(entry) ? entry : [entry];
		return entries.some(route => {
			if (route === '*') return true;

			const normalizedRoute = JkitTourHelpers._normalizeEntryRoute(route);
			const [_, expectedQuery = ''] = route.split('?');

			if (!expectedQuery) {
				return pathname === normalizedRoute || pathname.startsWith(`${normalizedRoute}/`);
			}

			if (pathname !== normalizedRoute && !pathname.startsWith(`${normalizedRoute}/`)) {
				return false;
			}

			return JkitTourHelpers._doesQueryMatch(expectedQuery, search);
		});
	}

	public static resolveEntryPath(entry: TJkitTourEntry): string {
		const entries = Array.isArray(entry) ? entry : [entry];
		return entries.find(route => route !== '*') ?? entries[0];
	}

	public static resolveBreakpoint(viewportWidth: number): TJkitTourBreakpoint {
		return viewportWidth < 1024 ? 'mobile' : 'desktop';
	}

	public static resolvePlacement(
		placement: TJkitTourStepPlacement | undefined,
		rect: DOMRect | null,
		viewportWidth: number,
		viewportHeight: number,
		tooltipSize: IJkitTourTooltipSize,
		tooltipMaxWidth: number
	): Exclude<TJkitTourStepPlacement, 'auto'> {
		if (!rect) return 'bottom';

		const tooltipWidth = Math.min(tooltipMaxWidth, viewportWidth - JkitTourHelpers._tooltipPadding * 2);
		const tooltipHeight = Math.max(120, tooltipSize.height || 220);
		const spaces = {
			top: rect.top - JkitTourHelpers._tooltipOffset - JkitTourHelpers._tooltipPadding,
			bottom: viewportHeight - rect.bottom - JkitTourHelpers._tooltipOffset - JkitTourHelpers._tooltipPadding,
			left: rect.left - JkitTourHelpers._tooltipOffset - JkitTourHelpers._tooltipPadding,
			right: viewportWidth - rect.right - JkitTourHelpers._tooltipOffset - JkitTourHelpers._tooltipPadding
		} as const;

		const fits = (candidate: Exclude<TJkitTourStepPlacement, 'auto'>): boolean => {
			if (candidate === 'top' || candidate === 'bottom') {
				return spaces[candidate] >= tooltipHeight;
			}

			return spaces[candidate] >= tooltipWidth;
		};

		const basePriority: Array<Exclude<TJkitTourStepPlacement, 'auto'>> = ['bottom', 'top', 'right', 'left'];
		const requested = placement && placement !== 'auto' ? [placement] : [];
		const candidates = [...requested, ...basePriority].filter((candidate, index, arr) => arr.indexOf(candidate) === index);

		for (const candidate of candidates) {
			if (fits(candidate)) return candidate;
		}

		return candidates.reduce((best, current) => (spaces[current] > spaces[best] ? current : best), candidates[0]);
	}

	public static resolveTooltipStyle(
		rect: DOMRect | null,
		viewportWidth: number,
		viewportHeight: number,
		placement: Exclude<TJkitTourStepPlacement, 'auto'>,
		tooltipSize: IJkitTourTooltipSize,
		tooltipMaxWidth: number
	): IJkitTourTooltipStyle {
		const width = Math.min(tooltipMaxWidth, viewportWidth - JkitTourHelpers._tooltipPadding * 2);
		const height = Math.max(120, tooltipSize.height || 220);
		const maxTop = Math.max(JkitTourHelpers._tooltipPadding, viewportHeight - height - JkitTourHelpers._tooltipPadding);
		const maxLeft = Math.max(JkitTourHelpers._tooltipPadding, viewportWidth - width - JkitTourHelpers._tooltipPadding);

		if (!rect) {
			return {
				top: JkitTourHelpers.clampValue(viewportHeight / 2 - height / 2, JkitTourHelpers._tooltipPadding, maxTop),
				left: JkitTourHelpers.clampValue(viewportWidth / 2 - width / 2, JkitTourHelpers._tooltipPadding, maxLeft),
				width,
				transform: 'none'
			};
		}

		const clampedCenteredLeft = JkitTourHelpers.clampValue(
			rect.left + rect.width / 2 - width / 2,
			JkitTourHelpers._tooltipPadding,
			maxLeft
		);
		const rightAlignedLeft = JkitTourHelpers.clampValue(
			rect.right + JkitTourHelpers._tooltipOffset,
			JkitTourHelpers._tooltipPadding,
			maxLeft
		);
		const leftAlignedLeft = JkitTourHelpers.clampValue(
			rect.left - width - JkitTourHelpers._tooltipOffset,
			JkitTourHelpers._tooltipPadding,
			maxLeft
		);
		const centeredTop = JkitTourHelpers.clampValue(rect.top + rect.height / 2 - height / 2, JkitTourHelpers._tooltipPadding, maxTop);

		if (placement === 'top') {
			return {
				top: JkitTourHelpers.clampValue(rect.top - JkitTourHelpers._tooltipOffset - height, JkitTourHelpers._tooltipPadding, maxTop),
				left: clampedCenteredLeft,
				width,
				transform: 'none'
			};
		}

		if (placement === 'left') {
			return {
				top: centeredTop,
				left: leftAlignedLeft,
				width,
				transform: 'none'
			};
		}

		if (placement === 'right') {
			return {
				top: centeredTop,
				left: rightAlignedLeft,
				width,
				transform: 'none'
			};
		}

		return {
			top: JkitTourHelpers.clampValue(rect.bottom + JkitTourHelpers._tooltipOffset, JkitTourHelpers._tooltipPadding, maxTop),
			left: clampedCenteredLeft,
			width,
			transform: 'none'
		};
	}

	public static parseRouterUrl(url: string): { pathname: string; search: string } {
		if (!url) return { pathname: '/', search: '' };

		const [pathAndSearch] = url.split('#');
		const [pathnameRaw, searchRaw = ''] = pathAndSearch.split('?');
		const pathname = pathnameRaw || '/';
		const search = searchRaw ? `?${searchRaw}` : '';
		return { pathname, search };
	}

	private static _normalizeEntryRoute(route: string): string {
		if (route === '*') return route;
		return route.split('#')[0].split('?')[0] || '/';
	}

	private static _normalizeSearch(search: string): string {
		return search.startsWith('?') ? search.slice(1) : search;
	}

	private static _doesQueryMatch(expectedQuery: string, currentSearch: string): boolean {
		if (!expectedQuery) return true;
		const expectedParams = new URLSearchParams(expectedQuery);
		const currentParams = new URLSearchParams(JkitTourHelpers._normalizeSearch(currentSearch));

		for (const [key, value] of expectedParams.entries()) {
			if (currentParams.get(key) !== value) return false;
		}

		return true;
	}
}
